import type { LineItem, Recurring } from '@/clients/payment-service'

/** Metadata key the payment-service sets on a one-off fee price to link it to its recurring counterpart. */
const TRIAL_PRICE_FOR_LOOKUP_KEY = 'trial_price_for_lookup_key'
const TRIAL_INTERVAL_KEY = 'trial_interval'
const TRIAL_INTERVAL_COUNT_KEY = 'trial_interval_count'

/**
 * A line item that may carry extra "due next" info when it was formed by
 * combining a one-off trial fee with its paired recurring subscription item.
 */
export type CombinedLineItem = LineItem & {
  /** Present only on combined trial items — describes the recurring charge after the trial. */
  dueNext?: {
    amount: number
    recurring: Recurring
  }
  /** Trial length normalised to days (e.g. 30 for a 30-day trial, 7 for a 7-day trial). */
  trialLengthDays?: number
}

/**
 * Info about a complex trial pair extracted from payment-service line items.
 * Used to thread pair identity to Stripe custom checkout components that don't
 * have access to price metadata.
 */
export type ComplexTrialPairInfo = {
  /** product_name of the one-off fee item (the item to keep visible). */
  feeProductName: string
  /** product_name of the recurring item (the item to hide). */
  recurringProductName: string
  /** The nickname from the fee item — used as display text. */
  displayName: string
  /** Trial length normalised to days. */
  trialLengthDays: number
  /** Renewal plan term derived from the recurring item's billing interval, e.g. "1-year". */
  renewalPlanLabel?: string
}

/**
 * Convert a trial interval + count into a number of days.
 * Aligns with how membership tracking reports trial length (always in days).
 */
export const trialIntervalToDays = (interval: string, count: number): number => {
  switch (interval.toLowerCase()) {
    case 'day':
      return count
    case 'week':
      return count * 7
    case 'month':
      return count * 30
    case 'year':
      return count * 365
    default:
      return count
  }
}

export const getRenewalPlanLabel = (recurring?: Recurring | null): string | undefined => {
  if (!recurring || recurring.interval_count < 1) return undefined

  return `${recurring.interval_count}-${recurring.interval.toLowerCase()}`
}

/**
 * Find complex trial pairs in a set of line items using the deterministic
 * `trial_price_for_lookup_key` metadata key set by the payment-service.
 *
 * Returns an array of `{ fee, recurring }` pairs. Items without the metadata
 * key, or whose referenced recurring item is missing, are ignored.
 */
export const findComplexTrialPairs = (
  lineItems: LineItem[],
): Array<{ fee: LineItem; recurring: LineItem; trialLengthDays: number }> => {
  const itemsByLookupKey = new Map(lineItems.map((li) => [li.price.lookup_key, li]))
  return lineItems
    .map((feeItem) => {
      const recurringKey = feeItem.price.metadata?.[TRIAL_PRICE_FOR_LOOKUP_KEY]
      if (!recurringKey) return null

      const recurringItem = itemsByLookupKey.get(recurringKey)
      if (!recurringItem) return null

      const interval = feeItem.price.metadata?.[TRIAL_INTERVAL_KEY] ?? 'day'
      const intervalCount = parseInt(feeItem.price.metadata?.[TRIAL_INTERVAL_COUNT_KEY] ?? '0', 10)
      const trialLengthDays = trialIntervalToDays(interval, intervalCount)

      return { fee: feeItem, recurring: recurringItem, trialLengthDays }
    })
    .filter((pair): pair is NonNullable<typeof pair> => pair !== null)
}

/**
 * Combine complex trial line items into a single display-friendly item.
 *
 * For each fee+recurring pair found via `trial_price_for_lookup_key` metadata:
 * - The fee item is kept as the base (amount, nickname, billing_description)
 * - A `dueNext` field is attached with the recurring item's amount and interval
 * - A `trialLengthDays` field is attached (normalised to days)
 * - The recurring item is removed from the list
 *
 * Items that are NOT part of a complex trial pass through unchanged.
 * When no complex trial pairs exist, this is a no-op that returns the
 * original array — safe for all checkout flows.
 */
export const combineComplexTrialLineItems = (lineItems: LineItem[]): CombinedLineItem[] => {
  const pairs = findComplexTrialPairs(lineItems)
  if (pairs.length === 0) return lineItems

  const recurringIds = new Set(pairs.map((p) => p.recurring.id))
  const feeToPair = new Map(pairs.map((p) => [p.fee.id, p]))

  return lineItems
    .filter((item) => !recurringIds.has(item.id))
    .map((item) => {
      const pair = feeToPair.get(item.id)
      if (!pair) return item

      return {
        ...item,
        dueNext: pair.recurring.price.recurring
          ? {
              amount: pair.recurring.price.unit_amount,
              recurring: pair.recurring.price.recurring,
            }
          : undefined,
        trialLengthDays: pair.trialLengthDays,
      }
    })
}

/**
 * Normalise a set of line items for membership GA tracking.
 *
 * For sessions that include a complex trial pair (fee + recurring linked by
 * `trial_price_for_lookup_key` metadata) the pair is collapsed into a single
 * tracking row, and the fee's `lookup_key` is remapped to the recurring base
 * SKU so GA emits one item keyed on the recurring SKU.
 *
 * Non-trial sessions: pass-through — `combined` is the original list,
 * `normalizedKeys` is the raw `lookup_key` array, `itemBundle` is the sorted
 * dash-joined string of those keys.
 */
export const normalizeMembershipTrackingItems = (
  lineItems: LineItem[],
): { combined: CombinedLineItem[]; normalizedKeys: string[]; itemBundle: string } => {
  const combined = combineComplexTrialLineItems(lineItems)
  const pairs = findComplexTrialPairs(lineItems)
  const feeKeyToRecurringKey = new Map(
    pairs.map((p) => [p.fee.price.lookup_key ?? '', p.recurring.price.lookup_key ?? '']),
  )
  const normalizedKeys = combined.map(
    (item) => feeKeyToRecurringKey.get(item.price.lookup_key ?? '') ?? item.price.lookup_key ?? '',
  )
  const itemBundle = normalizedKeys.slice().sort().join('-')
  return { combined, normalizedKeys, itemBundle }
}

/**
 * Extract complex trial pair info from payment-service line items.
 * Used to thread pair identity to Stripe custom checkout components
 * (OrderSummary) that only see Stripe's native line item shape.
 *
 * Returns undefined when there are no complex trial pairs (all non-trial flows).
 */
export const extractComplexTrialPairInfo = (lineItems: LineItem[]): ComplexTrialPairInfo | undefined => {
  const pairs = findComplexTrialPairs(lineItems)
  if (pairs.length === 0) return undefined

  const { fee, recurring, trialLengthDays } = pairs[0]
  return {
    feeProductName: fee.price.product_name ?? fee.price.nickname,
    recurringProductName: recurring.price.product_name ?? recurring.price.nickname,
    displayName: fee.price.nickname || fee.price.product_name || '',
    trialLengthDays,
    renewalPlanLabel: getRenewalPlanLabel(recurring.price.recurring),
  }
}
